import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { startWith } from 'rxjs';

type KeysOfType<T, U> = keyof { [K in keyof T as T[K] extends U ? K : never]: T[K] };

type FilteredKeys<T, U> = KeysOfType<T, U & string>;

type SearchFunctionOrProperty<T> = { key: FilteredKeys<T, string> } | { function: (term: string, item: T) => boolean };

function getSearchFunction<T>(searchFunctionOrProperty: SearchFunctionOrProperty<T>) {
    return 'key' in searchFunctionOrProperty
        ? // We know it's a string because the type enforces this
          (term: string, item: T) => (item[searchFunctionOrProperty.key] as string).toLowerCase().includes(term)
        : searchFunctionOrProperty.function;
}

export function getFilteredListSignal<T>(
    listSignal: Signal<T[]>,
    formControl: FormControl<string | null>,
    searchFunctionOrProperty: SearchFunctionOrProperty<T>,
    limit?: number,
): Signal<T[]> {
    const searchFunction = getSearchFunction(searchFunctionOrProperty);
    const searchSignal = toSignal(formControl.valueChanges.pipe(startWith(null)));
    return computed(() => {
        const items = listSignal();
        const term = searchSignal()?.trim()?.toLowerCase();
        const results = term ? items.filter((item) => searchFunction(term, item)) : items;
        return limit ? results.slice(0, limit) : results;
    });
}
